<script lang="ts">
  import { onMount } from 'svelte';
  import { createEventDispatcher } from 'svelte';
  import { api } from '../js/api.js';
  import { auth } from '../js/auth.js';
  import { toast } from '../js/toast.js';
  import { spinner } from '../js/spinner.js';
  import Modal from './Modal.svelte';
  import Checkout from './Checkout.svelte';
  import type { ApiResponse } from '../js/apiTypes.js';

  export let showModal = false;
  const dispatch = createEventDispatcher();

  let packs: any[] = [];
  let selectedPack: any = null;
  let displayCheckout = false;
  let modalWidth = 800;

  onMount(async () => {
    if (showModal) {
      await loadPacks();
    }
  });

  $: if (showModal) {
    loadPacks();
  }

  async function loadPacks() {
    spinner.show();
    try {
      const response = await api.getPaymentPlanPacks() as ApiResponse<{ packs?: unknown[] }>;
      if (response.success && response.data) {
        packs = response.data.packs ?? [];
      } else {
        toast.showNotification(false, response.message ?? 'Failed to load packs');
      }
    } catch (error) {
      toast.showNotification(false, 'Error loading packs');
      console.error(error);
    } finally {
      spinner.hide();
    }
  }

  function selectPack(pack: any) {
    selectedPack = pack;
    displayCheckout = true;
  }

  async function handleCheckoutSubmit(paymentMethodId: string): Promise<{ success?: boolean; message?: string }> {
    if (!selectedPack) return { success: false, message: 'No pack selected' };
    spinner.show();
    try {
      const teamUuid = $auth.user?.active_team_uuid;
      if (!teamUuid) {
        return { success: false, message: 'No active team found.' };
      }
      const response = await api.purchaseTeamPack(
        teamUuid,
        selectedPack.uuid,
        paymentMethodId
      ) as { success?: boolean; message?: string };
      if (response.success) {
        toast.showNotification(true, 'Pack purchased successfully!');
        displayCheckout = false;
        showModal = false;
        dispatch('purchased');
      } else {
        toast.showNotification(false, (response as any).message || 'Failed to purchase pack');
      }
      return response;
    } catch (error) {
      toast.showNotification(false, 'Error purchasing pack');
      console.error(error);
      return { success: false };
    } finally {
      spinner.hide();
    }
  }

  function formatPrice(price: number): string {
    return new Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  }
</script>

<Modal bind:showModal bind:modalWidth modalClass="pack-purchase-modal">
  <div slot="header">Purchase Brief Pack</div>
  <div slot="body">
    <p class="modal-description">Select a pack to purchase additional credits. Packs persist across billing periods.</p>
    
    <div class="packs-list">
      {#each packs as pack (pack.uuid)}
        <div class="pack-card">
          <div class="pack-header">
            <h4 class="pack-name">{pack.name}</h4>
            <div class="pack-price">{formatPrice(pack.price)}</div>
          </div>
          <div class="pack-briefs">{pack.brief_count} credits</div>
          <button
            class="pack-select-button"
            on:click={() => selectPack(pack)}
          >
            Purchase
          </button>
        </div>
      {/each}
    </div>
  </div>
  <div slot="footer">
    <button class="close-button" on:click={() => showModal = false}>Close</button>
  </div>
</Modal>

<Checkout
  bind:displayCheckout={displayCheckout}
  onSubmitPaymentMethod={handleCheckoutSubmit}
/>

<style>
  /* Force this modal width so 3 tiles stay side-by-side (overrides .ui.modal { width: 80% }) */
  :global(.ui.modal.pack-purchase-modal) {
    width: 800px !important;
    min-width: 800px !important;
  }

  .modal-description {
    color: var(--grey-secondary);
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }

  .packs-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.5rem;
    width: 100%;
  }

  @media (max-width: 768px) {
    :global(.ui.modal.pack-purchase-modal) {
      width: 100% !important;
      min-width: 0 !important;
    }
    .packs-list {
      grid-template-columns: 1fr;
    }
  }

  .pack-card {
    background: var(--white);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: 0;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.08);
    transition: box-shadow 0.2s ease, border-color 0.2s ease;
  }

  .pack-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: var(--primary);
  }

  .pack-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .pack-name {
    font-size: 1.125rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0;
  }

  .pack-price {
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--primary);
  }

  .pack-briefs {
    color: var(--grey-secondary);
    font-size: 0.875rem;
  }

  .pack-select-button {
    width: 100%;
    padding: 0.5rem 1rem;
    background: var(--primary);
    color: var(--white);
    border: 0;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.2);
    transition: background-color 0.2s ease;
  }

  .pack-select-button:hover:not(:disabled) {
    background: var(--darkprimary);
  }

  .pack-select-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .close-button {
    padding: 0.5rem 1.25rem;
    background: var(--white);
    color: var(--grey);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.08);
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  }

  .close-button:hover {
    background: var(--greyish);
    border-color: var(--grey-secondary);
    color: var(--grey);
  }

  .close-button:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }
</style>
